// Mock data for demonstration - shows realistic business scenarios

import {
  LeadStatus,
  LeadSource,
  OpportunityStage,
  AccountType,
  UserRole
} from '../types/salesforce';

import type {
  Lead,
  Opportunity,
  Account,
  Contact,
  User,
  PipelineMetrics,
  LeadMetrics,
  ChartDataPoint,
  PipelineByStage
} from '../types/salesforce';

// Generate realistic mock data
export class MockDataGenerator {
  private static getRandomDate(start: Date, end: Date): string {
    const date = new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()));
    return date.toISOString();
  }

  private static getRandomAmount(): number {
    return Math.floor(Math.random() * 100000) + 5000;
  }

  private static getRandomScore(): number {
    return Math.floor(Math.random() * 100);
  }

  // Mock leads data
  static generateLeads(count: number = 50): Lead[] {
    const firstNames = ['John', 'Jane', 'Michael', 'Sarah', 'David', 'Emily', 'Chris', 'Lisa', 'Robert', 'Amanda'];
    const lastNames = ['Smith', 'Johnson', 'Brown', 'Davis', 'Wilson', 'Moore', 'Taylor', 'Anderson', 'Thomas', 'Jackson'];
    const companies = ['TechCorp', 'DataSoft', 'CloudWorks', 'InnovateNow', 'FutureGen', 'SmartSolutions', 'NextLevel', 'ProActive', 'GrowthCo', 'Success Inc'];
    const sources = Object.values(LeadSource);
    const statuses = Object.values(LeadStatus);

    return Array.from({ length: count }, (_, i) => ({
      id: `lead_${i + 1}`,
      firstName: firstNames[Math.floor(Math.random() * firstNames.length)],
      lastName: lastNames[Math.floor(Math.random() * lastNames.length)],
      email: `lead${i + 1}@example.com`,
      company: companies[Math.floor(Math.random() * companies.length)],
      phone: `+1-555-${Math.floor(Math.random() * 9000) + 1000}`,
      status: statuses[Math.floor(Math.random() * statuses.length)],
      score: this.getRandomScore(),
      source: sources[Math.floor(Math.random() * sources.length)],
      assignedTo: `user_${Math.floor(Math.random() * 5) + 1}`,
      createdDate: this.getRandomDate(new Date(2023, 0, 1), new Date()),
      lastActivityDate: Math.random() > 0.3 ? this.getRandomDate(new Date(2023, 6, 1), new Date()) : undefined,
      convertedDate: Math.random() > 0.7 ? this.getRandomDate(new Date(2023, 8, 1), new Date()) : undefined,
      convertedOpportunityId: Math.random() > 0.7 ? `opp_${i + 1}` : undefined,
    }));
  }

  // Mock opportunities data
  static generateOpportunities(count: number = 75): Opportunity[] {
    const names = ['Enterprise Software Deal', 'Cloud Migration', 'Digital Transformation', 'Security Upgrade', 'Analytics Platform'];
    const stages = Object.values(OpportunityStage);
    const sources = Object.values(LeadSource);

    return Array.from({ length: count }, (_, i) => {
      const stage = stages[Math.floor(Math.random() * stages.length)];
      const isClosedWon = stage === OpportunityStage.CLOSED_WON;
      const isClosedLost = stage === OpportunityStage.CLOSED_LOST;
      const isClosed = isClosedWon || isClosedLost;

      // Realistic probability based on stage
      const stageProbabilities: Record<OpportunityStage, number> = {
        [OpportunityStage.PROSPECTING]: 10,
        [OpportunityStage.QUALIFICATION]: 20,
        [OpportunityStage.NEEDS_ANALYSIS]: 35,
        [OpportunityStage.VALUE_PROPOSITION]: 50,
        [OpportunityStage.PROPOSAL]: 70,
        [OpportunityStage.NEGOTIATION]: 85,
        [OpportunityStage.CLOSED_WON]: 100,
        [OpportunityStage.CLOSED_LOST]: 0,
      };

      return {
        id: `opp_${i + 1}`,
        name: `${names[Math.floor(Math.random() * names.length)]} - ${i + 1}`,
        amount: this.getRandomAmount(),
        stage,
        probability: stageProbabilities[stage] + Math.random() * 15 - 7.5, // Add some variance
        closeDate: this.getRandomDate(new Date(), new Date(2024, 11, 31)),
        accountId: `account_${Math.floor(Math.random() * 20) + 1}`,
        contactId: `contact_${Math.floor(Math.random() * 50) + 1}`,
        ownerId: `user_${Math.floor(Math.random() * 5) + 1}`,
        leadSource: sources[Math.floor(Math.random() * sources.length)],
        createdDate: this.getRandomDate(new Date(2023, 0, 1), new Date()),
        lastModifiedDate: this.getRandomDate(new Date(2023, 6, 1), new Date()),
        isClosed,
        isWon: isClosedWon,
      };
    });
  }

  // Mock accounts data
  static generateAccounts(count: number = 30): Account[] {
    const names = ['TechCorp Inc', 'DataSoft Solutions', 'CloudWorks LLC', 'InnovateNow Corp', 'FutureGen Technologies'];
    const industries = ['Technology', 'Healthcare', 'Finance', 'Manufacturing', 'Retail', 'Education'];
    const types = Object.values(AccountType);

    return Array.from({ length: count }, (_, i) => ({
      id: `account_${i + 1}`,
      name: `${names[Math.floor(Math.random() * names.length)]} ${i + 1}`,
      industry: industries[Math.floor(Math.random() * industries.length)],
      employees: Math.floor(Math.random() * 10000) + 50,
      annualRevenue: Math.floor(Math.random() * 100000000) + 1000000,
      website: `https://company${i + 1}.example.com`,
      phone: `+1-555-${Math.floor(Math.random() * 9000) + 1000}`,
      billingCity: 'San Francisco',
      billingState: 'CA',
      billingCountry: 'USA',
      type: types[Math.floor(Math.random() * types.length)],
      ownerId: `user_${Math.floor(Math.random() * 5) + 1}`,
    }));
  }

  // Mock pipeline metrics
  static generatePipelineMetrics(opportunities: Opportunity[]): PipelineMetrics {
    const totalValue = opportunities
      .filter(opp => !opp.isClosed)
      .reduce((sum, opp) => sum + opp.amount, 0);

    const totalOpportunities = opportunities.filter(opp => !opp.isClosed).length;
    const averageDealSize = totalOpportunities > 0 ? totalValue / totalOpportunities : 0;

    const closedOpportunities = opportunities.filter(opp => opp.isClosed);
    const wonOpportunities = opportunities.filter(opp => opp.isWon);
    const winRate = closedOpportunities.length > 0 ? (wonOpportunities.length / closedOpportunities.length) * 100 : 0;

    return {
      totalValue,
      totalOpportunities,
      averageDealSize,
      winRate,
      salesCycle: 45, // Average days
      conversionRate: 12.5, // Lead to opportunity conversion rate
    };
  }

  // Mock lead metrics
  static generateLeadMetrics(leads: Lead[]): LeadMetrics {
    const totalLeads = leads.length;
    const newLeads = leads.filter(lead => lead.status === LeadStatus.NEW).length;
    const qualifiedLeads = leads.filter(lead => lead.status === LeadStatus.QUALIFIED).length;
    const convertedLeads = leads.filter(lead => lead.status === LeadStatus.CONVERTED).length;

    const scores = leads.map(lead => lead.score).filter(score => score > 0);
    const averageScore = scores.length > 0 ? scores.reduce((sum, score) => sum + score, 0) / scores.length : 0;

    const distribution = [
      { range: '0-25', count: scores.filter(s => s <= 25).length },
      { range: '26-50', count: scores.filter(s => s > 25 && s <= 50).length },
      { range: '51-75', count: scores.filter(s => s > 50 && s <= 75).length },
      { range: '76-100', count: scores.filter(s => s > 75).length },
    ];

    return {
      totalLeads,
      newLeads,
      qualifiedLeads,
      convertedLeads,
      leadScore: {
        average: averageScore,
        distribution,
      },
    };
  }

  // Generate time series data for charts
  static generateTimeSeriesData(days: number = 30): ChartDataPoint[] {
    const data: ChartDataPoint[] = [];
    const baseValue = 50000;

    for (let i = 0; i < days; i++) {
      const date = new Date();
      date.setDate(date.getDate() - (days - 1 - i));

      // Generate realistic trending data
      const trend = Math.sin(i / 10) * 10000; // Cyclical pattern
      const randomVariation = (Math.random() - 0.5) * 20000; // Random variation
      const value = Math.max(0, baseValue + trend + randomVariation);

      data.push({
        date: date.toISOString().split('T')[0],
        value: Math.round(value),
        label: `Day ${i + 1}`,
      });
    }

    return data;
  }

  // Generate pipeline by stage data
  static generatePipelineByStage(opportunities: Opportunity[]): PipelineByStage[] {
    const stages = Object.values(OpportunityStage);

    return stages.map(stage => {
      const stageOpportunities = opportunities.filter(opp => opp.stage === stage);
      const totalValue = stageOpportunities.reduce((sum, opp) => sum + opp.amount, 0);
      const averageProbability = stageOpportunities.length > 0
        ? stageOpportunities.reduce((sum, opp) => sum + opp.probability, 0) / stageOpportunities.length
        : 0;

      return {
        stage,
        count: stageOpportunities.length,
        value: totalValue,
        probability: averageProbability,
      };
    });
  }
}

// Pre-generated mock data for immediate use
export const mockLeads = MockDataGenerator.generateLeads(50);
export const mockOpportunities = MockDataGenerator.generateOpportunities(75);
export const mockAccounts = MockDataGenerator.generateAccounts(30);
export const mockPipelineMetrics = MockDataGenerator.generatePipelineMetrics(mockOpportunities);
export const mockLeadMetrics = MockDataGenerator.generateLeadMetrics(mockLeads);
export const mockTimeSeriesData = MockDataGenerator.generateTimeSeriesData(30);
export const mockPipelineByStage = MockDataGenerator.generatePipelineByStage(mockOpportunities);

// Mock users data
export const mockUsers: User[] = [
  {
    id: 'user_1',
    name: 'Alice Johnson',
    email: 'alice@company.com',
    role: UserRole.SALES_MANAGER,
    isActive: true,
    profilePhotoUrl: undefined,
  },
  {
    id: 'user_2',
    name: 'Bob Smith',
    email: 'bob@company.com',
    role: UserRole.SALES_REP,
    isActive: true,
  },
  {
    id: 'user_3',
    name: 'Carol Williams',
    email: 'carol@company.com',
    role: UserRole.SALES_REP,
    isActive: true,
  },
  {
    id: 'user_4',
    name: 'David Brown',
    email: 'david@company.com',
    role: UserRole.MARKETING,
    isActive: true,
  },
  {
    id: 'user_5',
    name: 'Eva Davis',
    email: 'eva@company.com',
    role: UserRole.SALES_VP,
    isActive: true,
  },
];

export default MockDataGenerator;